// Subscribe to ephemeral typing + emit outbound typing/stop_typing.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { EphemeralClient } from './EphemeralClient';
import { conversationIdEqual } from './convMatch';
import type { EphemeralEvent } from './types';
import {
  deviceKeyFromBytes,
  formatTypingSubtitle,
  pruneTypingPeers,
  STOP_TYPING_IDLE_MS,
  type TypingPeer,
} from './typingFormat';
import { getShowTypingIndicators } from './typingSettings';
import { log } from '../log/log';

export interface UseEphemeralTypingOptions {
  /** Hex MLS / BE conversation id (32 chars). */
  readonly conversationId: string;
  readonly conversationIdBytes: Uint8Array;
  readonly getClient: () => EphemeralClient | null;
  /** Resolve a device id to a display label (1:1 → peer name). */
  readonly resolveDisplayName: (senderDevice: Uint8Array) => string;
  readonly enabled?: boolean;
  /** Fired when a peer STARTS typing in this conversation (the peer set
   *  transitions from empty → non-empty). Since this hook is only mounted for
   *  the open chat, the host can use it to play a typing chime for the active
   *  conversation. Throttling is the host's concern. */
  readonly onPeerTypingStart?: () => void;
}

export interface UseEphemeralTypingResult {
  /** Subtitle for `ChatRoom` header when peers are typing. */
  readonly typingSubtitle: string | null;
  readonly onComposerTextChange: (text: string) => void;
  /** Call when a message is sent or the composer is cleared. */
  readonly notifyComposerSent: () => void;
}

export function useEphemeralTyping(
  options: UseEphemeralTypingOptions,
): UseEphemeralTypingResult {
  const {
    conversationId,
    conversationIdBytes,
    getClient,
    resolveDisplayName,
    enabled = true,
    onPeerTypingStart,
  } = options;

  // A caller that decodes `conversationIdBytes` fresh every render would give
  // the effect a new Uint8Array reference each time, tearing down and
  // re-subscribing the WS (unsubscribe + stop_typing storm) on every render.
  // Derive a stable hex key and latch the bytes identity to it so the reference
  // only changes when the conversation actually changes.
  const conversationKey = deviceKeyFromBytes(conversationIdBytes);
  // biome-ignore lint/correctness/useExhaustiveDependencies: latch to conversationKey by design
  const conversationBytes = useMemo(() => conversationIdBytes, [conversationKey]);

  const peersRef = useRef<Map<string, TypingPeer>>(new Map());
  const [typingSubtitle, setTypingSubtitle] = useState<string | null>(null);

  // Keep the start-callback in a ref so `applyEvent` (and therefore the
  // subscribe effect) stays referentially STABLE even when the host passes a
  // fresh inline callback every render. Without this, the subscribe effect
  // re-ran on every render and its teardown cleared `peersRef` +
  // `setTypingSubtitle(null)` — so the typing indicator was set then wiped on
  // the very next render (the sound still played; the UI never showed).
  const onPeerTypingStartRef = useRef(onPeerTypingStart);
  onPeerTypingStartRef.current = onPeerTypingStart;

  const outboundActiveRef = useRef(false);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pruneTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const syncSubtitle = useCallback(() => {
    const pruned = pruneTypingPeers(peersRef.current);
    peersRef.current = pruned;
    setTypingSubtitle(formatTypingSubtitle([...pruned.values()]));
  }, []);

  const publishStop = useCallback(() => {
    if (!outboundActiveRef.current) return;
    outboundActiveRef.current = false;
    if (!getShowTypingIndicators()) return;
    const client = getClient();
    if (client == null) return;
    void client.publish(conversationBytes, 'stop_typing', {}).catch(() => {
      log.debug('ephemeral.typing.stop_failed');
    });
  }, [conversationBytes, getClient]);

  const scheduleStopTyping = useCallback(() => {
    if (stopTimerRef.current != null) {
      clearTimeout(stopTimerRef.current);
    }
    stopTimerRef.current = setTimeout(() => {
      stopTimerRef.current = null;
      publishStop();
    }, STOP_TYPING_IDLE_MS);
  }, [publishStop]);

  const notifyTypingStart = useCallback(() => {
    if (!getShowTypingIndicators()) return;
    const client = getClient();
    if (client == null) return;
    if (!outboundActiveRef.current) {
      outboundActiveRef.current = true;
      void client.publish(conversationBytes, 'typing', {}).catch(() => {
        log.debug('ephemeral.typing.start_failed');
        outboundActiveRef.current = false;
      });
      return;
    }
    scheduleStopTyping();
  }, [conversationBytes, getClient, scheduleStopTyping]);

  const onComposerTextChange = useCallback(
    (text: string) => {
      if (!enabled) return;
      if (text.trim().length === 0) {
        if (stopTimerRef.current != null) {
          clearTimeout(stopTimerRef.current);
          stopTimerRef.current = null;
        }
        publishStop();
        return;
      }
      notifyTypingStart();
      scheduleStopTyping();
    },
    [enabled, notifyTypingStart, publishStop, scheduleStopTyping],
  );

  const notifyComposerSent = useCallback(() => {
    if (stopTimerRef.current != null) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    publishStop();
  }, [publishStop]);

  const applyEvent = useCallback(
    (event: EphemeralEvent) => {
      if (event.type !== 'typing' && event.type !== 'stop_typing') {
        return;
      }
      const key = deviceKeyFromBytes(event.senderDevice);
      if (event.type === 'stop_typing') {
        peersRef.current.delete(key);
        syncSubtitle();
        return;
      }
      // A 0 → 1+ transition means a peer just STARTED typing — fire the
      // start callback (host plays the typing chime for the active chat).
      const wasIdle = peersRef.current.size === 0;
      const displayName = resolveDisplayName(event.senderDevice);
      peersRef.current.set(key, {
        deviceKey: key,
        displayName,
        lastActiveMs: Date.now(),
      });
      if (wasIdle && peersRef.current.size > 0) {
        try {
          onPeerTypingStartRef.current?.();
        } catch {
          /* a faulty host callback must not break typing */
        }
      }
      syncSubtitle();
    },
    [resolveDisplayName, syncSubtitle],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    type Client = NonNullable<ReturnType<typeof getClient>>;
    let teardown: (() => void) | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const subscribe = (client: Client): void => {
      client.subscribe([conversationBytes]);
      const unsub = client.onEvent((event) => {
        if (!conversationIdEqual(event.conversationId, conversationId)) {
          return;
        }
        applyEvent(event);
      });
      pruneTimerRef.current = setInterval(() => {
        syncSubtitle();
      }, 1_000);
      teardown = () => {
        unsub();
        client.unsubscribe([conversationBytes]);
        if (pruneTimerRef.current != null) {
          clearInterval(pruneTimerRef.current);
          pruneTimerRef.current = null;
        }
        if (stopTimerRef.current != null) {
          clearTimeout(stopTimerRef.current);
          stopTimerRef.current = null;
        }
        publishStop();
        peersRef.current.clear();
        setTypingSubtitle(null);
      };
    };

    const client = getClient();
    if (client != null) {
      subscribe(client);
    } else {
      // The ephemeral WS client is often NOT ready on the first render
      // after a reload (it reconnects asynchronously once the session is
      // restored). `getClient` is a stable ref, so the effect won't
      // re-run when it connects — poll until it's available, then
      // subscribe. Without this, typing/presence never appears post-reload.
      pollTimer = setInterval(() => {
        const c = getClient();
        if (c != null) {
          if (pollTimer != null) {
            clearInterval(pollTimer);
            pollTimer = null;
          }
          subscribe(c);
        }
      }, 500);
    }

    return () => {
      if (pollTimer != null) clearInterval(pollTimer);
      teardown?.();
    };
  }, [
    enabled,
    conversationId,
    conversationBytes,
    getClient,
    applyEvent,
    syncSubtitle,
    publishStop,
  ]);

  return {
    typingSubtitle,
    onComposerTextChange,
    notifyComposerSent,
  };
}
